import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { AuthService } from './../../../services/auth.service';
import { Component, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { FNFResponse } from '../../../models/response';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { ConfirmationDialogComponent } from '../../controls/fnf-confirmation/confirmation-dialog/confirmation-dialog.component';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-change-password',
  standalone: true,
  imports: [ReactiveFormsModule, MatIcon, CommonModule],
  templateUrl: './change-password.component.html',
  styleUrl: './change-password.component.css',
})
export class ChangePasswordComponent {
  authService = inject(AuthService);
  ngxSpinnerService = inject(NgxSpinnerService);
  changePasswordForm: FormGroup;
  currentUser: any;
  showOldPassword = false;
  showNewPassword = false;
  showConfirmPassword = false;
  passwordStrength = 0;

  constructor(
    private dialog: MatDialog,
    private toastr: ToastrService,
    private fb: FormBuilder,
    private router: Router,
  ) {
    this.changePasswordForm = this.fb.group(
      {
        oldPassword: ['', [Validators.required, Validators.minLength(8)]],
        newPassword: [
          '',
          [
            Validators.required,
            Validators.minLength(8),
            Validators.pattern(
              /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
            ),
            this.validateNewPasswordNotOld.bind(this),
          ],
        ],
        confirmPassword: ['', Validators.required],
      },
      { validators: this.matchPasswords('newPassword', 'confirmPassword') },
    );

    // `validateNewPasswordNotOld` lives on newPassword but reads oldPassword,
    // and Angular only re-runs a control's validators when that control
    // changes — so editing the current password would leave a stale
    // "same as old password" error behind. Re-validate it by hand.
    this.changePasswordForm.get('oldPassword')?.valueChanges.subscribe(() => {
      this.changePasswordForm.get('newPassword')?.updateValueAndValidity({ emitEvent: false });
    });
  }

  ngOnInit() {
    // this.getCurrentUser();
  }

  checkStrength(password: string): number {
    let strength = 0;

    if (password.length >= 8) strength++; // Minimum length
    if (/[A-Z]/.test(password)) strength++; // Uppercase
    if (/[a-z]/.test(password)) strength++; // Lowercase
    if (/\d/.test(password)) strength++; // Numbers
    if (/[@$!%*?&]/.test(password)) strength++; // Special Characters

    return strength; // Strength score between 0-5
  }

  getPasswordStrengthText(): string {
    switch (this.passwordStrength) {
      case 1:
        return 'Weak';
      case 2:
        return 'Fair';
      case 3:
        return 'Good';
      case 4:
        return 'Strong';
      case 5:
        return 'Very Strong';
      default:
        return '';
    }
  }

  onPasswordInput() {
    const password = this.changePasswordForm.get('newPassword')?.value;
    this.passwordStrength = this.checkStrength(password);
  }

  getCurrentUser() {
    this.ngxSpinnerService.show();
    this.authService.getCurrentUser().subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.ngxSpinnerService.hide();
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.ngxSpinnerService.hide();
          return;
        }
        this.currentUser = response.data;
        this.ngxSpinnerService.hide();
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
      },
    });
  }

  // Opens a confirmation dialog to logout the user
  logoutConfirm() {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        message:
          'Password changed successfully. This will logout the system. Are you sure you want to proceed?',
        buttonText: {
          ok: 'Yes, proceed',
          // cancel: 'No, Cancel',
        },
      },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.authService.logout();
        this.router.navigate(['/login']);
      } else {
        this.changePasswordForm.reset();
      }
    });
  }

  // Custom validator to ensure new password is not the same as the old password
  validateNewPasswordNotOld(control: any) {
    return control.value !== this.changePasswordForm?.get('oldPassword')?.value
      ? null
      : { sameAsOldPassword: true };
  }

  // Custom validator to check if new password matches confirm password
  matchPasswords(newPasswordKey: string, confirmPasswordKey: string) {
    return (formGroup: FormGroup) => {
      const newPassword = formGroup.controls[newPasswordKey];
      const confirmPassword = formGroup.controls[confirmPasswordKey];

      return newPassword.value === confirmPassword.value ? null : { passwordsMismatch: true };
    };
  }

  onSubmit() {
    if (this.changePasswordForm.valid) {
      let data = {
        old_password: this.changePasswordForm.value.oldPassword,
        new_password: this.changePasswordForm.value.newPassword,
        confirm_password: this.changePasswordForm.value.confirmPassword,
      };
      this.ngxSpinnerService.show();
      this.authService.changePasswordUser(data).subscribe({
        next: (response: FNFResponse<any>) => {
          if (!response.data && response.error) {
            this.toastr.error(response.error.error?.message || response.error.message, 'Error');
            this.ngxSpinnerService.hide();
            return;
          } else if (!response.data) {
            this.toastr.error('No data found!', 'Error');
            this.ngxSpinnerService.hide();
            return;
          }
          // this.toastr.success(response.data.message, 'Success');
          this.ngxSpinnerService.hide();
          this.logoutConfirm();
        },
        error: () => {
          this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
          this.ngxSpinnerService.hide();
        },
      });
    }
  }

  togglePasswordVisibility(field: string) {
    if (field === 'old') {
      this.showOldPassword = !this.showOldPassword;
    } else if (field === 'new') {
      this.showNewPassword = !this.showNewPassword;
    } else if (field === 'confirm') {
      this.showConfirmPassword = !this.showConfirmPassword;
    }
  }
}
